'use client';

import { Button, Card, InlineToast, Input } from '@clickt/brand';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { ThemedLogo } from '../components/ThemedLogo.tsx';
import { authClient } from '../lib/auth-client.ts';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/reset-password' }) as { token?: string };
  const token = search.token ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (vars: { newPassword: string }) => {
      const { error } = await authClient.resetPassword({
        newPassword: vars.newPassword,
        token,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void navigate({ to: '/login' });
    },
    onError: (err: { message?: string }) => {
      setErrorMessage(err.message ?? 'Reset failed. The link may have expired.');
    },
  });

  const tokenMissing = !token;

  return (
    <div className="min-h-screen flex flex-col surface-dotted">
      <header className="flex items-center px-6 lg:px-10 py-6">
        <a
          href="https://clickt.link"
          className="flex items-center rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
          aria-label="clickt home"
        >
          <ThemedLogo className="h-8 w-auto" />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-3 text-center">
            <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
              Set a new password.
            </h1>
            <p className="text-body-md text-cool-gray">You'll be signed out of other sessions.</p>
          </div>

          <Card padding="lg">
            {tokenMissing ? (
              <InlineToast
                tone="danger"
                title="Reset link is invalid."
                description="The link is missing its token. Request a new reset email."
              />
            ) : (
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (password !== confirm) {
                    setErrorMessage("Passwords don't match.");
                    return;
                  }
                  setErrorMessage(null);
                  mutation.mutate({ newPassword: password });
                }}
              >
                {errorMessage ? (
                  <InlineToast
                    tone="danger"
                    title="Couldn't reset password."
                    description={errorMessage}
                  />
                ) : null}
                <Input
                  label="New password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  autoFocus
                  placeholder="At least 8 characters"
                  hint="8+ characters."
                />
                <Input
                  label="Confirm new password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Same password again"
                />
                <Button type="submit" size="lg" className="w-full" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Resetting…' : 'Reset password'}
                  {!mutation.isPending && <ArrowRight className="h-4 w-4" />}
                </Button>
              </form>
            )}
          </Card>

          <p className="text-center text-[14px] text-cool-gray">
            <Link
              to="/login"
              className="text-midnight font-medium hover:text-electric transition-colors duration-fast"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
